/**
 * SecurityTab — HIPAA & security due diligence per entity (Phase 3).
 *
 *   GET  /api/fdr/entities/:id/security             posture: BAA, docs, SOC 2 reviews, gaps
 *   POST /api/fdr/entities/:id/baa                  record BAA (supersedes prior active)
 *   POST /api/fdr/entities/:id/security-documents   upload; soc2_report auto-runs the agent
 *   POST /api/fdr/soc2-reviews/:id/confirm          human confirms; exceptions → findings
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_API_URL;
const inputCls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
const btnPrimary = 'px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50';
const GAP_CFG = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
};
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-slate-200/60 shadow-sm ${className}`}>{children}</div>
);

export default function SecurityTab({ entityId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBaa, setShowBaa] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fdr/entities/${entityId}/security`, { credentials: 'include' });
      const d = await res.json();
      if (d.success) setData(d);
    } catch {}
    setLoading(false);
  }, [entityId]);
  useEffect(() => { setLoading(true); load(); return () => clearTimeout(pollRef.current); }, [load]);

  // Poll while any review is extracting.
  useEffect(() => {
    if (data?.soc2_reviews?.some(r => r.status === 'extracting')) {
      pollRef.current = setTimeout(load, 5000);
    }
  }, [data, load]);

  const upload = async (docType) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setNote({ err: true, text: 'Choose a file first.' }); return; }
    setUploading(true); setNote(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const res = await fetch(`${API}/api/fdr/entities/${entityId}/security-documents`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await res.json();
      if (d.success) {
        setNote({ err: false, text: docType === 'soc2_report' ? 'Uploaded — the review agent is reading it now.' : 'Uploaded.' });
        fileRef.current.value = '';
        load();
      } else setNote({ err: true, text: d.message || 'Upload failed' });
    } catch { setNote({ err: true, text: 'Upload failed' }); }
    setUploading(false);
  };

  const confirmReview = async (id) => {
    const res = await fetch(`${API}/api/fdr/soc2-reviews/${id}/confirm`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
    }).then(r => r.json()).catch(() => null);
    if (res?.success) setNote({ err: false, text: `Review confirmed — ${res.findings_created} exception(s) opened as findings.` });
    load();
  };

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-orange-500 animate-spin" />
    </div>
  );
  if (!data) return null;

  const activeBaa = data.baas?.find(b => b.status === 'active');

  return (
    <div className="space-y-4">
      {/* Gaps banner */}
      {data.gaps?.length > 0 && (
        <div className="space-y-1.5">
          {data.gaps.map((g, i) => (
            <div key={i} className={`text-xs border rounded-xl px-3 py-2 ${GAP_CFG[g.severity] || GAP_CFG.warning}`}>
              <span className="font-bold uppercase text-[10px] tracking-wide mr-1.5">{g.severity}</span>{g.note}
            </div>
          ))}
        </div>
      )}
      {note && (
        <div className={`text-xs rounded-xl px-3 py-2 ${note.err ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{note.text}</div>
      )}

      {/* BAA registry */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-slate-800">Business Associate Agreement</h3>
          <button onClick={() => setShowBaa(true)}
            className="text-xs font-bold text-orange-600 hover:text-orange-700">
            {activeBaa ? 'Record new BAA (supersedes)' : '+ Record BAA'}
          </button>
        </div>
        {activeBaa ? (
          <div className="text-sm text-slate-600">
            <div className="flex items-center gap-3 flex-wrap text-xs">
              <span className="font-semibold text-slate-800">Executed {activeBaa.executed_date ? String(activeBaa.executed_date).split('T')[0] : '—'}</span>
              <span>Breach notice: {activeBaa.breach_notice_hours ? `${activeBaa.breach_notice_hours}h` : 'not stated'}</span>
              {[['has_subcontractor_flowdown', 'Flow-down'], ['has_return_destroy_phi', 'Return/destroy PHI'],
                ['has_hhs_availability', 'HHS availability'], ['has_termination_clause', 'Termination'],
                ['has_safeguards_clause', 'Safeguards']].map(([k, l]) => (
                <span key={k} className={`px-2 py-0.5 rounded-full font-bold ${activeBaa[k] ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                  {l} {activeBaa[k] ? '✓' : '✗'}
                </span>
              ))}
            </div>
            {activeBaa.phi_categories && <p className="text-xs text-slate-400 mt-1.5">PHI: {activeBaa.phi_categories}</p>}
          </div>
        ) : (
          <p className="text-xs text-slate-400">No BAA on record. Required before any PHI flows (45 CFR 164.502(e)).</p>
        )}
      </Card>

      {/* Evidence vault + upload */}
      <Card className="p-5">
        <h3 className="text-sm font-bold text-slate-800 mb-2">Security evidence</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt"
            className="text-xs text-slate-500 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs file:font-bold hover:file:bg-slate-200" />
          <button disabled={uploading} onClick={() => upload('soc2_report')}
            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-sm disabled:opacity-50">
            {uploading ? 'Uploading…' : 'Upload as SOC 2 (runs review)'}
          </button>
          {['pen_test', 'policy', 'insurance_cert'].map(t => (
            <button key={t} disabled={uploading} onClick={() => upload(t)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 capitalize">
              {t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        {data.documents?.length > 0 && (
          <div className="mt-3 space-y-1">
            {data.documents.map(d => (
              <div key={d.id} className="flex items-center gap-3 text-xs text-slate-600 py-1 border-b border-slate-50 last:border-0">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-bold uppercase text-[9px] tracking-wide">{d.doc_type.replace(/_/g, ' ')}</span>
                <span className="flex-1 truncate">{d.file_name}</span>
                <span className="text-slate-300">{String(d.created_at).split('T')[0]}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* SOC 2 reviews */}
      {data.soc2_reviews?.map(r => (
        <Card key={r.id} className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">SOC 2 Review</h3>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              r.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' :
              r.status === 'pending_review' ? 'bg-amber-50 text-amber-700' :
              r.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
            }`}>{r.status === 'extracting' ? 'Agent reading…' : r.status.replace(/_/g, ' ')}</span>
          </div>

          {r.status === 'failed' && <p className="text-xs text-red-600 mt-2">{r.extraction_notes}</p>}

          {['pending_review', 'confirmed'].includes(r.status) && (
            <div className="mt-2 text-xs text-slate-600 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-slate-800 uppercase">{r.report_type?.replace(/_/g, ' ') || '?'}</span>
                <span>{r.auditor || 'auditor unknown'}</span>
                <span className={`px-2 py-0.5 rounded-full font-bold ${r.opinion === 'unqualified' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {r.opinion || 'opinion?'}
                </span>
                <span>Period: {r.period_start ? String(r.period_start).split('T')[0] : '?'} → {r.period_end ? String(r.period_end).split('T')[0] : '?'}{r.stale ? ' (STALE — bridge letter needed)' : ''}</span>
                <span>TSC: {(Array.isArray(r.tsc_scope) ? r.tsc_scope : []).join(', ') || '—'}</span>
              </div>

              {(Array.isArray(r.exceptions) ? r.exceptions : []).length > 0 && (
                <div>
                  <div className="font-bold text-red-700 text-[10px] uppercase tracking-wide">Exceptions ({r.exceptions.length})</div>
                  {r.exceptions.map((e, i) => (
                    <div key={i} className="mt-1 bg-red-50/50 rounded-lg px-3 py-2">
                      <div className="font-semibold text-slate-700">{e.control}</div>
                      <div className="text-slate-600">{e.exception}</div>
                      {e.quote && <blockquote className="border-l-2 border-red-300 pl-2 italic text-slate-500 mt-1">"{e.quote}"</blockquote>}
                    </div>
                  ))}
                </div>
              )}

              {(Array.isArray(r.cuecs) ? r.cuecs : []).length > 0 && (
                <div>
                  <div className="font-bold text-indigo-700 text-[10px] uppercase tracking-wide">Complementary user entity controls — OUR obligations ({r.cuecs.length})</div>
                  {r.cuecs.slice(0, 8).map((c, i) => (
                    <div key={i} className="mt-1 text-slate-600">• {c.description || c.control}</div>
                  ))}
                </div>
              )}

              {r.extraction_notes && <p className="text-slate-400">{r.extraction_notes}</p>}

              {r.status === 'pending_review' && (
                <button onClick={() => confirmReview(r.id)} className={`${btnPrimary} mt-1`}>
                  Confirm review — open {(Array.isArray(r.exceptions) ? r.exceptions : []).length} exception(s) as findings
                </button>
              )}
            </div>
          )}
        </Card>
      ))}

      {showBaa && <BaaModal entityId={entityId} onClose={() => setShowBaa(false)} onDone={() => { setShowBaa(false); load(); }} />}
    </div>
  );
}

function BaaModal({ entityId, onClose, onDone }) {
  const [form, setForm] = useState({
    executed_date: '', breach_notice_hours: '', phi_categories: '',
    has_subcontractor_flowdown: false, has_return_destroy_phi: false,
    has_hhs_availability: false, has_termination_clause: false, has_safeguards_clause: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const body = { ...form, breach_notice_hours: form.breach_notice_hours === '' ? null : Number(form.breach_notice_hours) };
      const res = await fetch(`${API}/api/fdr/entities/${entityId}/baa`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.success) onDone(); else setErr(d.message || 'Failed');
    } catch { setErr('Failed to record BAA'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">Record BAA</h3>
          <p className="text-xs text-slate-400 mt-0.5">Tick only clauses actually present in the executed agreement</p>
        </div>
        <form onSubmit={submit} className="px-8 py-5 space-y-4">
          {err && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Executed date</label>
              <input type="date" value={form.executed_date} onChange={e => setForm({ ...form, executed_date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Breach notice SLA (hours)</label>
              <input type="number" min="1" placeholder="e.g. 72" value={form.breach_notice_hours}
                onChange={e => setForm({ ...form, breach_notice_hours: e.target.value })} className={inputCls} />
            </div>
          </div>
          <input placeholder="PHI categories handled (e.g. claims, MBIs, clinical notes)" value={form.phi_categories}
            onChange={e => setForm({ ...form, phi_categories: e.target.value })} className={inputCls} />
          <div className="space-y-1.5 text-sm text-slate-700">
            {[['has_safeguards_clause', 'Appropriate safeguards / Security Rule compliance'],
              ['has_subcontractor_flowdown', 'Subcontractor flow-down (equivalent terms in writing)'],
              ['has_return_destroy_phi', 'Return or destroy PHI at termination'],
              ['has_hhs_availability', 'Books and records available to HHS'],
              ['has_termination_clause', 'Termination for material breach']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={e => setForm({ ...form, [k]: e.target.checked })}
                  className="rounded border-slate-300 text-orange-600 focus:ring-orange-500/30" />
                {l}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Recording…' : 'Record BAA'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
